import React, {forwardRef, useContext, useMemo} from 'react';

import {FloatingMenu, FloatingMenuBody, MenuItem} from '@pb/kit-component-menu';
import {hashedClasses} from '@pb/kit-util-with-css-module';

import {DropdownMenuContext} from './dropdown-menu-context';
import styles from './styles.module.styl';

import type {DropdownMenuContextType} from './dropdown-menu-context';
import type {UseArrowReturnType} from '@pb/kit-component-floating';
import type {FloatingMenuProps} from '@pb/kit-component-menu';


const classNames = hashedClasses(styles);

type MenuProps = Omit<FloatingMenuProps, 'open' | 'flip' | 'ref'>;

/**
 * $FlowIgnoreMe WHY: Flow cannot resolve multiple extends
 **/
export interface DropdownMenuBaseProps extends DropdownMenuContextType, MenuProps {
    /**
     * Title in dropdown menu. Unclickable.
     */
    title?: string,

    /**
     * Body component
     * */
    bodyComponent?: typeof FloatingMenuBody,

    /**
     * shouldUnmount=false will keep the dropdown content in the DOM even when it's not shown
     *
     * @deprecated It will be removed in 6 months 09-15-2025. render your modals outside of the dropdown, see example in the Storybook
     * */
    shouldUnmount?: boolean,

    arrow: UseArrowReturnType['Component'],
}

/**
 * Dropdown menu popover. Contains floating element, should be used with useDropdownMenu hook.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/hover-dropdown-menu--documentation}
 * */
export const DropdownMenuBase = forwardRef<HTMLDivElement, DropdownMenuBaseProps>((props, ref) => {

    const DropdownMenuContextParentValue = useContext(DropdownMenuContext);

    const {
        getContext,
        children,
        portalProps = DropdownMenuContextParentValue?.portalProps,
        floatingFocusManagerProps = DropdownMenuContextParentValue?.floatingFocusManagerProps,
        title,
        maxHeight = DropdownMenuContextParentValue?.maxHeight,
        maxWidth = DropdownMenuContextParentValue?.maxWidth,
        type = DropdownMenuContextParentValue?.type || 'ABSOLUTE',
        bodyComponent = DropdownMenuContextParentValue?.bodyComponent || FloatingMenuBody,
        shouldUnmount = DropdownMenuContextParentValue?.shouldUnmount ?? true,
        onItemSelect,
        arrow: Arrow,
        ...restProps
    } = props;

    const contextValue = useMemo(
        () => ({
            bodyComponent,
            type,
            maxHeight,
            maxWidth,
            floatingFocusManagerProps,
            portalProps,
            onItemSelect,
            shouldUnmount,
        }),
        [
            bodyComponent,
            type,
            maxHeight,
            maxWidth,
            floatingFocusManagerProps,
            portalProps,
            onItemSelect,
            shouldUnmount,
        ],
    );

    const context = getContext();

    const BodyComponent = bodyComponent;

    return (
        <FloatingMenu
            {...restProps}
            open={!shouldUnmount}
            floatingFocusManagerProps={{
                modal: false,
                ...floatingFocusManagerProps,
            }}
            flip={context.placement.startsWith('top-')}
            portalProps={portalProps}
            ref={ref}
            getContext={getContext}>
            <DropdownMenuContext.Provider value={contextValue}>
                <Arrow getContext={getContext}/>
                <BodyComponent>
                    {title ? <MenuItem className={classNames['dropdown-menu-title']}>{title}</MenuItem> : null}
                    {children}
                </BodyComponent>
            </DropdownMenuContext.Provider>
        </FloatingMenu>
    );
});

DropdownMenuBase.displayName = 'DropdownMenuBase';
